import React, { FC } from 'react';
import i18n from 'i18n-js';
import Svg from '@styles/icons/svg';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';
import { isWeb, machineStatusName } from '@helpers';
import { IconConfig } from '@components/operatorPanel/interfaces';
import TouchableElement from '@components/common/touchableElement';
import { OPERATOR_PANEL_HOME_SCREEN_BUTTON_HEIGHT, OPERATOR_PANEL_HOME_SCREEN_BUTTON_WIDTH } from '@components/operatorPanel/constants/operatorPanel.constants';
import { SimpleOnPress } from '@interfaces/base';
import { PanelProps } from '@components/operatorPanel/components/panel';

export interface OperatorPanelButtonProps extends Partial<SimpleOnPress>, Pick<PanelProps, 'CC' | 'scale' | 'font'> {
  text: string;
  iconConfig?: IconConfig;
  iconsConfig?: IconConfig[];
  machineStatus?: string;
  buttonContainerStyle?: StyleProp<ViewStyle>;
  iconContainerStyle?: StyleProp<ViewStyle>;
  textColor?: string;
  textFont?: keyof typeof initialFont;
  customButtonBgColor?: string;
  testID?: string;
}

const OperatorPanelButton: FC<OperatorPanelButtonProps> = ({
  CC,
  iconConfig,
  iconsConfig,
  text,
  machineStatus,
  buttonContainerStyle,
  iconContainerStyle,
  textColor = 'Label/Dark/AlwaysLight Primary',
  textFont = 'Headline Large',
  customButtonBgColor,
  onPress,
  scale = initialScale,
  font = initialFont,
  testID,
}) => {
  const boxShadowStyle = getBoxShadowStyle({ type: 'Very Wide', scheme: 'dark' });

  const bgColor = CC(customButtonBgColor ?? `Button/Light/Operator Panel [${machineStatusName[machineStatus]}]`);

  if (!bgColor) return null;

  return (
    <TouchableElement onPress={onPress} testID={testID}>
      {({ boxStyle, pressHoverColor }) => (
        <View
          style={[
            boxStyle,
            {
              backgroundColor: bgColor,
              paddingTop: scale(16),
              paddingBottom: scale(19),
              paddingHorizontal: scale(15),
              height: scale(OPERATOR_PANEL_HOME_SCREEN_BUTTON_HEIGHT),
              alignItems: 'center',
              borderRadius: scale(18),
              width: scale(OPERATOR_PANEL_HOME_SCREEN_BUTTON_WIDTH),
              gap: scale(18),
              ...boxShadowStyle,
            },
            isWeb && { cursor: 'pointer' },
            buttonContainerStyle,
          ]}
        >
          {(!!iconConfig || !!iconsConfig) && (
            <View style={[{ marginTop: scale(17), alignItems: 'center', justifyContent: 'center' }, iconContainerStyle]}>
              {iconConfig && <Svg {...iconConfig} />}
              {iconsConfig?.length && iconsConfig.map((c) => <Svg key={c.icon} {...c} />)}
            </View>
          )}
          <Text style={[font[textFont], { color: CC(textColor), textAlign: 'center' }]}>{i18n.t(text)}</Text>
        </View>
      )}
    </TouchableElement>
  );
};

export default OperatorPanelButton;
